import React, {useEffect, useState} from "react";
import {
    FormHelperText,
    ListItemIcon,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import borders from "../../styles/borders";
import MeetingRoomBookingDropdown from "./MeetingRoomBookingDropdown";
import {MeetingRoomType, MeetingType} from "../../Utils/Enums";
import {useTranslation} from "react-i18next";
import {useDeviceMediaType} from "../../hooks/useDeviceMediaType";
import {FormControl, InputLabel, MenuItem, Select} from "@material-ui/core";
import SeatConfigImagePreview from "./SeatConfigImagePreview";
import dayjs from "dayjs";
import {useFileUpload} from "../../hooks/useFileUpload";

interface MeetingRoomBookingSettingsProps {
    roomId: string,
    meetingRoomId: string,
    maxParticipants: number | null
    meetingRoomType: MeetingRoomType
    settings: MeetingRoomBookingSettingsData
    setSettings: (newData: Partial<MeetingRoomBookingSettingsData>) => void
    selectedDates: Date[]
    allSeatConfigurations?: string[]
    daysInAdvance?: number
    seatConfigImageId?: string
    setSeatConfigDaysInAdvance?: (days: number) => void
}

export interface MeetingRoomBookingSettingsData {
    meetingName: string
    participantNumber: number | null
    meetingType: MeetingType
    visitors: string
    seatConf?: string | null
}

export interface SeatOptionI {
    seatConfId?: string;
    name: string;
    image?: string;
    daysInAdvance: number;
}

function MeetingRoomBookingSettings(props: MeetingRoomBookingSettingsProps) {
    const initialOption = 'no-change';
    const {
        roomId,
        meetingRoomId,
        maxParticipants,
        meetingRoomType,
        settings,
        setSettings,
        selectedDates,
        allSeatConfigurations,
        daysInAdvance,
        seatConfigImageId,
        setSeatConfigDaysInAdvance
    } = props;
    const {isNoFullscreen} = useDeviceMediaType();
    const [selectedOption, setSelectedOption] = useState(initialOption);
    const [currentSeatConfig, setCurrentSeatConfig] = useState<string | undefined>(undefined);
    const [isDaysInAdvanceNotPossible, setIsDaysInAdvanceNotPossible] = useState<boolean>(false);
    const [seatConfigs, setSeatConfigs] = useState<string[]>([]);
    const [seatConfigImageUrl, setSeatConfigImageUrl] = useState<string>('');
    const {getImageUrl} = useFileUpload();

    useEffect(() => {
        if (allSeatConfigurations) {
            setSeatConfigs(allSeatConfigurations);
        }
    }, [roomId, meetingRoomId, allSeatConfigurations]);

    useEffect(() => {
        if (seatConfigImageId && roomId) {
            getImageUrl(roomId, seatConfigImageId)
                .then((url) => setSeatConfigImageUrl(url || ""))
        }
    }, [seatConfigImageId]);

    useEffect(() => {
        if (settings.seatConf && settings.seatConf?.length > 0) {
            setSelectedOption(seatConfigs.find((seatConfig) => seatConfig === settings.seatConf) ?? initialOption);
        }
    }, [settings.seatConf, seatConfigs]);

    useEffect(() => {
        if (setSeatConfigDaysInAdvance) {
            if (selectedOption === initialOption) {
                setIsDaysInAdvanceNotPossible(false);
                setSeatConfigDaysInAdvance(0)
            } else {
                if (daysInAdvance) {
                    setSeatConfigDaysInAdvance(daysInAdvance)
                }
            }
        }
    }, [currentSeatConfig, selectedOption]);

    useEffect(() => {
        if (selectedDates.length > 0) {
            setIsDaysInAdvanceNotPossible((daysInAdvance ?? 0) > getDifferenceFromTodayToMeetingDay());
        } else {
            // no date selected
            setIsDaysInAdvanceNotPossible(false);
        }

    }, [selectedDates])

    useEffect(() => {
        if (selectedOption) {
            const seatConfig = findSeatConfig(selectedOption);
            setCurrentSeatConfig(seatConfig);
        }
    }, [selectedOption]);

    const getDifferenceFromTodayToMeetingDay = () => {
        const today = dayjs();
        const meetingDate = dayjs(selectedDates.sort((a, b) => dayjs(a).diff(dayjs(b)))[0]);
        const differenceInDays = meetingDate.diff(today, 'day');

        return differenceInDays;
    }

    const findSeatConfig = (value: string) => {
        return allSeatConfigurations?.find((seatConfig) => seatConfig === value);
    }

    const handleChange = (event: any) => {
        setSelectedOption(event.target.value);
        const seatConfig = findSeatConfig(event.target.value);
        setSettings({seatConf: seatConfig});
    };

    const handleMeetingNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setSettings({meetingName: event.target.value});
    }

    const handleVisitorsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setSettings({visitors: event.target.value});
    }

    function handleMeetingTypeChange(meetingType: MeetingType) {
        setSettings({meetingType: meetingType});
    }

    const handleParticipantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const newValueNumber = parseInt(event.target.value);

        const isNumberValidInput = Number.isInteger(newValueNumber);
        const newData: Partial<MeetingRoomBookingSettingsData> = {participantNumber: isNumberValidInput ? newValueNumber : null}
        setSettings(newData);
    }

    const showWarning = maxParticipants !== null
        && settings.participantNumber !== null
        && settings.participantNumber > maxParticipants

    const {t} = useTranslation();
    return (
        <div style={{display: "flex", flexDirection: "row"}} data-testid={"time-window-table"}>
            <TableContainer sx={{
                minWidth: "300px",
                maxHeight: "500px", // increased for visitors field, TODO check if needed
                borderRadius: borders.mediumBorderRadius,
                overflow: "auto"
            }}>
                <Table stickyHeader>
                    {!isNoFullscreen &&
                        <TableHead>
                            <TableRow>
                                <TableCell colSpan={2}
                                           sx={{fontSize: "large"}}>
                                    {t('multibookingdialog_meetingroom_settings_title')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    }
                    <TableBody data-testid={"time-window-table-body"}>
                        <TableRow>
                            <TableCell align={'center'} colSpan={2} width={1}
                                       data-testid={"multibookingdialog_meetingroom_settings_meeting_name"}>
                                <TextField style={{width: "100%", backgroundColor: "white"}}
                                           label={t('multibookingdialog_meetingroom_settings_meeting_name')}
                                           variant="filled" required value={settings.meetingName}
                                           onChange={handleMeetingNameChange}
                                           inputProps={{maxLength: 30}}/>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell align={'center'} colSpan={2} width={1}
                                       data-testid={"multibookingdialog_meetingroom_settings_participant_number"}>
                                <TextField style={{width: "100%", backgroundColor: "white"}}
                                           label={t('multibookingdialog_meetingroom_settings_participant_number')}
                                           variant="filled"
                                           value={settings.participantNumber ?? ""}
                                           helperText={showWarning ? t("multibookingdialog_meetingroom_settings_participant_number_recommendation", {
                                               capacity: maxParticipants
                                           }) : ""}
                                           onChange={handleParticipantChange} required/>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell align={'center'} colSpan={2} width={1}
                                       data-testid={"multibookingdialog_meetingroom_settings_dropdown"}>
                                <MeetingRoomBookingDropdown meetingTypeState={settings.meetingType}
                                                            setMeetingTypeState={handleMeetingTypeChange}/>
                            </TableCell>
                        </TableRow>
                        {meetingRoomType === MeetingRoomType.VISITORS_CENTER ?
                            <TableRow>
                                <TableCell align={'center'} colSpan={2} width={1}>
                                    <TextField style={{width: "100%", backgroundColor: "white"}}
                                               label={t("general_visitors")}
                                               variant="filled" value={settings.visitors}
                                               onChange={handleVisitorsChange}
                                               inputProps={{maxLength: 100}}/>
                                </TableCell>
                            </TableRow> : null
                        }
                        {seatConfigImageUrl && (
                            <SeatConfigImagePreview imagePath={seatConfigImageUrl} showThumbnail={false}/>
                        )}
                        {seatConfigImageUrl && seatConfigs.length > 0 && (
                            <TableRow>
                                <TableCell align={'center'} colSpan={2} width={1}>
                                    <FormControl fullWidth>
                                        <InputLabel
                                            id="select-label">{t('meeting_room_booking_settings_config_option')}</InputLabel>
                                        <Select
                                            disabled={isDaysInAdvanceNotPossible}
                                            labelId="select-label"
                                            value={selectedOption}
                                            onChange={handleChange}
                                            SelectDisplayProps={{
                                                style: {
                                                    display: 'flex'
                                                }
                                            }}
                                        >
                                            <MenuItem value="no-change">
                                                <ListItemIcon sx={{
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}></ListItemIcon>
                                                <ListItemText sx={{textAlign: 'left'}}>
                                                    {t('meeting_room_booking_settings_config_option_default')}
                                                </ListItemText>
                                            </MenuItem>
                                            {seatConfigs?.sort().map((menuItem, index) => {
                                                return <MenuItem key={index} value={menuItem}>
                                                    <ListItemText
                                                        sx={{textAlign: 'left'}}
                                                    >
                                                        {menuItem}
                                                    </ListItemText>
                                                </MenuItem>
                                            })}
                                        </Select>
                                        {isDaysInAdvanceNotPossible && (
                                            <FormHelperText error={isDaysInAdvanceNotPossible}>
                                                {t("seat-config-days-in-advance-message", {days: daysInAdvance})}
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>);
}

export default MeetingRoomBookingSettings
